import { useApiMutation, useApiQuery } from '@/shared/hooks';
import { endpoints } from '@/shared/api/endpoints';
import { getEndpoint, getMutationMethod } from '@/shared/utils/getUrl';
import { toast } from '@/shared/store/useToastStore';
import type {
  GenerateReportRequest,
  ReportResponse,
  ReportsList,
  ReportsParams,
  ReportTemplate,
} from './types';

/**
 * Хук для генерации отчета
 */
export const useGenerateReportMutation = () => {
  return useApiMutation<ReportResponse, GenerateReportRequest>(
    getEndpoint(endpoints.reports.generate),
    getMutationMethod(endpoints.reports.generate),
    {
      invalidateQueries: [['reports', 'list']],
      onSuccess: () => {
        toast.info('Отчет формируется. Вы получите уведомление по завершении.');
      },
      onError: (error) => {
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (error as { message?: string })?.message ||
          'Ошибка при формировании отчета';
        toast.error(errorMessage);
      },
    }
  );
};

/**
 * Хук для получения доступных шаблонов отчетов
 */
export const useReportTemplatesQuery = (enabled: boolean = true) => {
  return useApiQuery<ReportTemplate[]>(
    ['reports', 'templates'],
    getEndpoint(endpoints.reports.templates),
    {
      enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Хук для получения статуса отчета по ID
 */
export const useReportStatusQuery = (reportId: string | null, enabled: boolean = true) => {
  return useApiQuery<ReportResponse>(
    ['reports', 'status', reportId ?? 'no-report'],
    reportId ? getEndpoint(endpoints.reports.byId, reportId) : '',
    {
      enabled: enabled && !!reportId,
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return false;
        // Опрашиваем каждые 2 секунды, если статус generating
        if (data.status === 'generating') {
          return 2000;
        }
        // Прекращаем опрос, если статус не generating и не completed
        return false;
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
};

/**
 * Хук для получения списка отчетов
 */
export const useReportsListQuery = (params?: ReportsParams) => {
  return useApiQuery<ReportsList>(
    ['reports', 'list', params ? JSON.stringify(params) : 'all'],
    getEndpoint(endpoints.reports.list, params),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 секунд кеширования
    }
  );
};
